import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../../stores/store";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FormSelect from '../../Generic/FormSelect';
import CriterionValueGroup from '../../Generic/CriterionValueGroup';
import {
    Alert, CircularProgress, Card, CardContent, Typography,
    Button, Box, Accordion, AccordionSummary, AccordionDetails,
    Chip, IconButton, Tooltip
} from '@mui/material';
import { Row, Form } from 'react-bootstrap';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import ClearIcon from '@mui/icons-material/Clear';

const SubmitToolFeature = () => {
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submittedCount, setSubmittedCount] = useState(0);

    useSetMenu(mainStore.UserSubmissionMenuType, mainStore.SubmitToolFeatureMenuNo);

    // Selection state
    const [panelOptions, setPanelOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const [toolOptions, setToolOptions] = useState([]);

    const [selectedPanel, setSelectedPanel] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    // Criteria grouped by weight coefficient (shared across all tools)
    const [criteriaW1, setCriteriaW1] = useState([]);
    const [criteriaW05, setCriteriaW05] = useState([]);
    const [criteriaW033, setCriteriaW033] = useState([]);

    // Per-tool criteria value selections: { toolId: { w1: [...], w05: [...], w033: [...] } }
    const [toolSelections, setToolSelections] = useState({});

    // Expanded tool accordion
    const [expandedTool, setExpandedTool] = useState(null);

    const [loadingTools, setLoadingTools] = useState(false);
    const [loadingCriteria, setLoadingCriteria] = useState(false);

    // Load panels on mount
    useEffect(() => {
        const loadPanels = async () => {
            try {
                const panelsRes = await axiosPrivate.get("/Panels/SelectOptions");
                setPanelOptions(panelsRes.data);
            } catch (err) {
                console.error("Error loading panels:", err);
            }
        };
        loadPanels();
    }, [axiosPrivate]);

    // Handle panel change
    const handlePanelChange = useCallback(async (option) => {
        setSelectedPanel(option);
        setSelectedCategory(null);
        setCategoryOptions([]);
        setSelectedSubcategory(null);
        setSubcategoryOptions([]);
        setToolOptions([]);
        setCriteriaW1([]);
        setCriteriaW05([]);
        setCriteriaW033([]);
        setToolSelections({});
        setExpandedTool(null);

        if (!option) return;
        try {
            const res = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${option.value}`);
            setCategoryOptions(typeof res.data === "string" && res.data === "No records" ? [] : res.data);
        } catch (err) {
            console.error("Error loading categories:", err);
        }
    }, [axiosPrivate]);

    // Handle category change
    const handleCategoryChange = useCallback(async (option) => {
        setSelectedCategory(option);
        setSelectedSubcategory(null);
        setSubcategoryOptions([]);
        setToolOptions([]);
        setCriteriaW1([]);
        setCriteriaW05([]);
        setCriteriaW033([]);
        setToolSelections({});
        setExpandedTool(null);

        if (!option) return;
        try {
            const res = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${option.value}`);
            setSubcategoryOptions(typeof res.data === "string" && res.data === "No records" ? [] : res.data);
        } catch (err) {
            console.error("Error loading subcategories:", err);
        }
    }, [axiosPrivate]);

    // Handle subcategory change - load tools and criteria with values
    const handleSubcategoryChange = useCallback(async (option) => {
        setSelectedSubcategory(option);
        setToolOptions([]);
        setCriteriaW1([]);
        setCriteriaW05([]);
        setCriteriaW033([]);
        setToolSelections({});
        setExpandedTool(null);

        if (!option) return;

        setLoadingTools(true);
        setLoadingCriteria(true);
        try {
            const toolsRes = await axiosPrivate.get(`/ToolPassports/Public/WithoutFeaturesBySubcategory/${option.value}`);
            const availableTools = typeof toolsRes.data === "string" && toolsRes.data === "No records" ? [] : toolsRes.data;
            setToolOptions(availableTools);
        } catch (err) {
            console.error("Error loading tools:", err);
        } finally {
            setLoadingTools(false);
        }

        try {
            const criteriaRes = await axiosPrivate.get(`/Criterias/Public/WithValuesBySubcategory/${option.value}`);
            const data = criteriaRes.data;
            let allCriteria = [];
            if (typeof data === "string" && data === "No records") {
                allCriteria = [];
            } else if (!Array.isArray(data)) {
                allCriteria = [data];
            } else {
                allCriteria = data;
            }
            setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
            setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
            setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));
        } catch (err) {
            console.error("Error loading criteria:", err);
        } finally {
            setLoadingCriteria(false);
        }
    }, [axiosPrivate]);

    // Get selections for a specific tool
    const getToolSelection = (toolId) => {
        return toolSelections[toolId] || { w1: [], w05: [], w033: [] };
    };

    // Set selections for a specific tool and weight group
    const setToolSelectionForWeight = (toolId, weightGroup, values) => {
        setToolSelections(prev => ({
            ...prev,
            [toolId]: {
                ...getToolSelection(toolId),
                [weightGroup]: values
            }
        }));
    };

    // Clear all selections for a tool
    const clearToolSelections = (toolId) => {
        setToolSelections(prev => {
            const newSelections = { ...prev };
            delete newSelections[toolId];
            return newSelections;
        });
    };

    // Count selected criteria values for a tool
    const getToolSelectionCount = (toolId) => {
        const sel = getToolSelection(toolId);
        return sel.w1.length + sel.w05.length + sel.w033.length;
    };

    // Get tools with selections
    const toolsWithSelections = toolOptions.filter(tool => getToolSelectionCount(tool.value) > 0);

    // Total selections across all tools
    const totalSelections = Object.keys(toolSelections).reduce((acc, toolId) => {
        return acc + getToolSelectionCount(toolId);
    }, 0);

    // Validate form
    const isFormValid = selectedPanel && selectedCategory && selectedSubcategory && toolsWithSelections.length > 0;

    // Submit handler - submit all tools with selections
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (toolsWithSelections.length === 0) {
            enqueueSnackbar(t("pleaseSelectAtLeastOneCriteriaValue") || "Please select criteria values for at least one tool", { variant: "warning" });
            return;
        }

        setSubmitting(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            for (const tool of toolsWithSelections) {
                const sel = getToolSelection(tool.value);
                const allCriteriaValueIds = [...sel.w1, ...sel.w05, ...sel.w033];

                if (allCriteriaValueIds.length === 0) continue;

                try {
                    const payload = {
                        toolPassportId: parseInt(tool.value),
                        criteriaValueIds: allCriteriaValueIds,
                        subcategoryId: parseInt(selectedSubcategory.value),
                        categoryId: parseInt(selectedCategory.value),
                        panelId: parseInt(selectedPanel.value)
                    };

                    await axiosPrivate.post("/ToolFeatures/Submit", payload);
                    successCount++;
                } catch (error) {
                    console.error(`Error submitting tool feature for ${tool.label}:`, error);
                    errorCount++;
                }
            }

            if (successCount > 0) {
                setSubmittedCount(successCount);
                setSubmitted(true);
                if (errorCount > 0) {
                    enqueueSnackbar(t("someSubmissionsFailed", { success: successCount, failed: errorCount }) || `${successCount} submitted, ${errorCount} failed`, { variant: "warning" });
                }
            } else {
                enqueueSnackbar(t("submitToolFeatureError"), { variant: "error" });
            }
        } catch (error) {
            console.error("Error submitting tool features:", error);
            enqueueSnackbar(t("submitToolFeatureError"), { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form for another submission
    const resetForm = async () => {
        setSubmitted(false);
        setSubmittedCount(0);
        setToolSelections({});
        setExpandedTool(null);

        // Reload tools list to exclude submitted tools
        if (selectedSubcategory) {
            setLoadingTools(true);
            try {
                const toolsRes = await axiosPrivate.get(`/ToolPassports/Public/WithoutFeaturesBySubcategory/${selectedSubcategory.value}`);
                const availableTools = typeof toolsRes.data === "string" && toolsRes.data === "No records" ? [] : toolsRes.data;
                setToolOptions(availableTools);
            } catch (err) {
                console.error("Error reloading tools:", err);
            } finally {
                setLoadingTools(false);
            }
        }
    };

    // Render criteria section for a tool
    const renderCriteriaSection = (toolId, criteriaList, weightGroup, bgColor, icon, title, subtitle) => {
        const selection = getToolSelection(toolId);
        const selectedIds = selection[weightGroup] || [];

        return (
            <div style={{ marginBottom: "1rem" }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    background: bgColor,
                    borderRadius: '6px 6px 0 0',
                    marginBottom: '8px'
                }}>
                    {icon}
                    <div>
                        <h5 style={{ margin: 0, color: '#fff', fontSize: '0.9em', fontWeight: '600' }}>
                            {title}
                        </h5>
                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7em' }}>
                            {subtitle}
                        </span>
                    </div>
                    <span style={{
                        marginLeft: 'auto',
                        background: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        padding: '3px 8px',
                        borderRadius: '10px',
                        fontSize: '0.7em',
                        fontWeight: '600'
                    }}>
                        {selectedIds.length} / {criteriaList.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                    </span>
                </div>
                {criteriaList.length === 0 ? (
                    <div style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: '#94a3b8',
                        background: '#f8fafc',
                        borderRadius: '0 0 6px 6px',
                        border: '1px dashed #e2e8f0',
                        fontSize: '0.85em'
                    }}>
                        {t("noCriteriaAvailable")}
                    </div>
                ) : (
                    criteriaList.map((crit) => (
                        <CriterionValueGroup
                            key={crit.id}
                            criterion={crit}
                            selectedValueIds={selectedIds}
                            setSelectedValueIds={(newIds) => setToolSelectionForWeight(toolId, weightGroup, newIds)}
                        />
                    ))
                )}
            </div>
        );
    };

    // Success state
    if (submitted) {
        return (
            <div className="container py-4">
                <Card className="text-center">
                    <CardContent sx={{ py: 5 }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" gutterBottom>
                            {t("submitToolFeatureSuccess")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" className="mb-2">
                            {t("toolFeaturesSubmittedCount", { count: submittedCount }) || `${submittedCount} tool(s) submitted successfully`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" className="mb-4">
                            {t("submissionPendingApproval")}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={resetForm}
                            >
                                {t("submitAnother")}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/MySubmissions')}
                            >
                                {t("viewMySubmissions")}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container py-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">
                    <FeaturedPlayListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t("submitToolFeatureTitle")}
                </h4>
            </div>

            <Alert severity="info" className="mb-4" icon={<InfoOutlinedIcon />}>
                {t("submitToolFeatureInfoMultiple") || "Select a subcategory to see available tools. Click on a tool to expand and select criteria values. You can submit features for multiple tools at once."}
            </Alert>

            <Card>
                <CardContent>
                    <Form onSubmit={handleSubmit}>
                        {/* Panel, Category, Subcategory Selection */}
                        <Row className="mb-3">
                            <FormSelect
                                label="panel"
                                name="panelId"
                                options={panelOptions}
                                onChange={handlePanelChange}
                                value={selectedPanel}
                                colSize={4}
                                isClearable={true}
                            />
                            <FormSelect
                                label="category"
                                name="categoryId"
                                options={categoryOptions}
                                onChange={handleCategoryChange}
                                value={selectedCategory}
                                colSize={4}
                                isClearable={true}
                                disabled={!selectedPanel}
                            />
                            <FormSelect
                                label="subcategory"
                                name="subcategoryId"
                                options={subcategoryOptions}
                                onChange={handleSubcategoryChange}
                                value={selectedSubcategory}
                                colSize={4}
                                isClearable={true}
                                disabled={!selectedCategory}
                            />
                        </Row>

                        {/* Loading indicators */}
                        {(loadingTools || loadingCriteria) && (
                            <div className="text-center py-4">
                                <CircularProgress size={30} />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {loadingTools ? t("loadingTools") || "Loading tools..." : t("loadingCriteria") || "Loading criteria..."}
                                </Typography>
                            </div>
                        )}

                        {/* Tools List */}
                        {!loadingTools && !loadingCriteria && selectedSubcategory && toolOptions.length > 0 && (
                            <div style={{ marginTop: '16px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {t("availableTools")} ({toolOptions.length})
                                    </Typography>
                                    {toolsWithSelections.length > 0 && (
                                        <Chip
                                            color="primary"
                                            label={`${toolsWithSelections.length} ${t("toolsSelected")} (${totalSelections} ${t("criteriaValues")})`}
                                        />
                                    )}
                                </Box>

                                {toolOptions.map((tool) => {
                                    const selectionCount = getToolSelectionCount(tool.value);
                                    const isExpanded = expandedTool === tool.value;

                                    return (
                                        <Accordion
                                            key={tool.value}
                                            expanded={isExpanded}
                                            onChange={(e, expanded) => setExpandedTool(expanded ? tool.value : null)}
                                            sx={{
                                                mb: 1,
                                                border: selectionCount > 0 ? '2px solid #22c55e' : '1px solid #e2e8f0',
                                                borderRadius: '8px !important',
                                                '&:before': { display: 'none' },
                                                boxShadow: selectionCount > 0 ? '0 2px 8px rgba(34, 197, 94, 0.15)' : 'none'
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    background: selectionCount > 0 ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : '#fafafa',
                                                    borderRadius: isExpanded ? '8px 8px 0 0' : '8px'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                                                    <BuildIcon color={selectionCount > 0 ? "success" : "action"} />
                                                    <Typography fontWeight={500}>{tool.label}</Typography>
                                                    {selectionCount > 0 && (
                                                        <>
                                                            <Chip
                                                                size="small"
                                                                color="success"
                                                                label={`${selectionCount} ${t("selected")}`}
                                                                sx={{ ml: 'auto' }}
                                                            />
                                                            <Tooltip title={t("clearSelections")}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        clearToolSelections(tool.value);
                                                                    }}
                                                                >
                                                                    <ClearIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 2 }}>
                                                {/* Mandatory Criteria (W = 1.00) */}
                                                {renderCriteriaSection(
                                                    tool.value,
                                                    criteriaW1,
                                                    'w1',
                                                    'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                                                    <PriorityHighIcon sx={{ color: '#fff', fontSize: 20 }} />,
                                                    `${t("Mandatory Criteria")} (W = 1.00)`,
                                                    t("Highest priority criteria")
                                                )}

                                                {/* Criteria (W = 0.50) */}
                                                {renderCriteriaSection(
                                                    tool.value,
                                                    criteriaW05,
                                                    'w05',
                                                    'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                                                    <TuneIcon sx={{ color: '#fff', fontSize: 20 }} />,
                                                    `${t("Criteria")} (W = 0.50)`,
                                                    t("Medium priority criteria")
                                                )}

                                                {/* Criteria (W = 0.33) */}
                                                {renderCriteriaSection(
                                                    tool.value,
                                                    criteriaW033,
                                                    'w033',
                                                    'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                                    <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 20 }} />,
                                                    `${t("Criteria")} (W = 0.33)`,
                                                    t("Lower priority criteria")
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </div>
                        )}

                        {/* No tools message */}
                        {!loadingTools && selectedSubcategory && toolOptions.length === 0 && (
                            <Alert severity="warning" className="mt-3">
                                {t("noToolsAvailableForFeatures") || "No tools available for adding features. All tools in this subcategory already have features or pending submissions."}
                            </Alert>
                        )}

                        {/* No criteria message */}
                        {!loadingCriteria && selectedSubcategory && criteriaW1.length === 0 && criteriaW05.length === 0 && criteriaW033.length === 0 && toolOptions.length > 0 && (
                            <Alert severity="warning" className="mt-3">
                                {t("noCriteriaFound") || "No criteria found for this subcategory."}
                            </Alert>
                        )}

                        {/* Submit Button */}
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div>
                                {toolsWithSelections.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        {t("readyToSubmit", { tools: toolsWithSelections.length, criteria: totalSelections }) ||
                                            `Ready to submit: ${toolsWithSelections.length} tool(s) with ${totalSelections} criteria value(s)`}
                                    </Typography>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/MySubmissions')}
                                    disabled={submitting}
                                >
                                    {t("cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={!isFormValid || submitting}
                                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <FeaturedPlayListIcon />}
                                >
                                    {submitting ? t("submitting") : `${t("submitToolFeature")} (${toolsWithSelections.length})`}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
};

export default SubmitToolFeature;
